import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import querystring from 'query-string';

import { when } from '@/service/Switch';
import i18n from '@/plugins/i18n';
import { auth0Module } from '@/store/dataModules/auth0Module';

const baseUrl = process.env.VUE_APP_API_URL;
const xkey = process.env.VUE_APP_X_API_KEY;
// memoru
const memoruBaseUrl = process.env.VUE_APP_MEMORU_API_URL;
const memoruXkey = process.env.VUE_APP_MEMORU_X_API_KEY;

const client = axios.create();

interface Response {
  status: number;
  config: {
    url: string;
  };
  data: {
    [key: string]: string;
  };
}

interface ErrorResponse {
  status: number;
  config: {
    url: string;
  };
  data: {
    data: {
      message: string;
    };
    status: string;
  };
}

const getHeaders = async () => {
  const token = await auth0Module.getAuth0Token();
  // console.log('token', token)
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json; charset=utf-8',
    'Accept-Language': getAcceptLanguage(),
  };
  if (xkey) {
    headers['x-api-key'] = xkey;
  }
  return headers;
};

const getMemoruHeaders = async () => {
  const token = await auth0Module.getAuth0Token();
  // console.log('token', token)
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json; charset=utf-8',
    'Accept-Language': getAcceptLanguage(),
  };
  if (memoruXkey) {
    headers['x-api-key'] = memoruXkey;
  }
  return headers;
};

const getAcceptLanguage = () => {
  const locale = localStorage.getItem('user-locale') || i18n.global.locale.value || 'en';

  if (locale === 'vi' || locale === 'en' || locale === 'ja') {
    return locale;
  }

  return 'en';
};

export enum ErrorType {
  UNAUTHORIZED = 'unauthorized',
  FIRSTLOGIN = 'firstLogin',
  NO_TOKEN = 'no_token',
}

client.interceptors.response.use(
  async response => {
    try {
      const r: Response = response as Response;
      // return response
      if (r.config.url) {
        if (r.status !== 200) {
          throw Promise.reject(response);
        }
        if (r.data.error) {
          if (r.data.error === 'E003') {
            throw ErrorType.FIRSTLOGIN;
          } else if (r.data.error === 'E014') {
          } else {
            // Swal.fire(r.data.error, r.data.message, 'warning');
            const errorCode = String(r.data.error);
            const message = getErrorMessage(errorCode);
            Swal.fire(`${message}`, ``, 'warning');
            throw Promise.reject(response);
          }
        }
        return response;
      } else {
        if (r.data.status !== '0') {
          // status != 0の時は、何かしらのエラー
          throw Promise.reject(response);
        }
        return response;
      }
    } catch (error) {
      throw error;
    }
  },
  async error => {
    try {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        if (axiosError.response.status >= 500) {
          // 認証エラーの時は、サインイン画面に飛ばす
          const status = String(axiosError.response.status);
          const message = String((axiosError.response.data as any)?.message ?? axiosError.message);
          Swal.fire(status, message, 'error');
          throw Promise.reject(error);
        }
        if (axiosError.response.status === 401) {
          window.location.href = '/signin';
          throw Promise.reject(ErrorType.UNAUTHORIZED);
        }
        if (axiosError.response.status === 400) {
          // 認証エラーの時は、サインイン画面に飛ばす
          const status = String(axiosError.response.status);
          const message = getErrorMessage(String((axiosError.response.data as any)?.error ?? 'E000'));
          Swal.fire(status, message, 'error');
          throw Promise.reject(error);
        }
      }
      throw Promise.reject(error);
    } catch (error) {
      throw Promise.reject(error);
    }
  }
);

const getErrorMessage = (errorCode: string): string => {
  return when(errorCode)
    .on(
      v => v === 'E000',
      () => i18n.global.t('errors.E000')
    )
    .on(
      v => v === 'E001',
      () => i18n.global.t('errors.E001')
    )
    .on(
      v => v === 'E002',
      () => i18n.global.t('errors.E002')
    )
    .on(
      v => v === 'E003',
      () => i18n.global.t('errors.E003')
    )
    .on(
      v => v === 'E004',
      () => i18n.global.t('errors.E004')
    )
    .on(
      v => v === 'E005',
      () => i18n.global.t('errors.E005')
    )
    .on(
      v => v === 'E006',
      () => i18n.global.t('errors.E006')
    )
    .on(
      v => v === 'E007',
      () => i18n.global.t('errors.E007')
    )
    .on(
      v => v === 'E008',
      () => i18n.global.t('errors.E008')
    )
    .on(
      v => v === 'E009',
      () => i18n.global.t('errors.E009')
    )
    .on(
      v => v === 'E010',
      () => i18n.global.t('errors.E010')
    )
    .on(
      v => v === 'E011',
      () => i18n.global.t('errors.E011')
    )
    .on(
      v => v === 'E012',
      () => i18n.global.t('errors.E012')
    )
    .on(
      v => v === 'E013',
      () => i18n.global.t('errors.E013')
    )
    .on(
      v => v === 'E014',
      () => i18n.global.t('errors.E014')
    )
    .on(
      v => v === 'E015',
      () => i18n.global.t('errors.E015')
    )
    .on(
      v => v === 'E016',
      () => i18n.global.t('errors.E016')
    )
    .on(
      v => v === 'E017',
      () => i18n.global.t('errors.E017')
    )
    .on(
      v => v === 'E018',
      () => i18n.global.t('errors.E018')
    )
    .on(
      v => v === 'E019',
      () => i18n.global.t('errors.E019')
    )
    .on(
      v => v === 'E020',
      () => i18n.global.t('errors.E020')
    )
    .on(
      v => v === 'E021',
      () => i18n.global.t('errors.E021')
    )
    .on(
      v => v === 'E022',
      () => i18n.global.t('errors.E022')
    )
    .on(
      v => v === 'E023',
      () => i18n.global.t('errors.E023')
    )
    .on(
      v => v === 'E024',
      () => i18n.global.t('errors.E024')
    )
    .on(
      v => v === 'E025',
      () => i18n.global.t('errors.E025')
    )
    .on(
      v => v === 'E026',
      () => i18n.global.t('errors.E026')
    )
    .on(
      v => v === 'E027',
      () => i18n.global.t('errors.E027')
    )
    .on(
      v => v === 'E028',
      () => i18n.global.t('errors.E028')
    )
    .on(
      v => v === 'E029',
      () => i18n.global.t('errors.E029')
    )
    .on(
      v => v === 'E030',
      () => i18n.global.t('errors.E030')
    )
    .on(
      v => v === 'E031',
      () => i18n.global.t('errors.E031')
    )
    .on(
      v => v === 'E032',
      () => i18n.global.t('errors.E032')
    )
    .on(
      v => v === 'E033',
      () => i18n.global.t('errors.E033')
    )
    .on(
      v => v === 'E034',
      () => i18n.global.t('errors.E034')
    )
    .on(
      v => v === 'E035',
      () => i18n.global.t('errors.E035')
    )
    .on(
      v => v === 'E036',
      () => i18n.global.t('errors.E036')
    )
    .on(
      v => v === 'E037',
      () => i18n.global.t('errors.E037')
    )
    .on(
      v => v === 'E038',
      () => i18n.global.t('errors.E038')
    )
    .on(
      v => v === 'E039',
      () => i18n.global.t('errors.E039')
    )
    .on(
      v => v === 'E040',
      () => i18n.global.t('errors.E040')
    )
    .on(
      v => v === 'E041',
      () => i18n.global.t('errors.E041')
    )
    .on(
      v => v === 'E042',
      () => i18n.global.t('errors.E042')
    )
    .on(
      v => v === 'E043',
      () => i18n.global.t('errors.E043')
    )
    .on(
      v => v === 'E044',
      () => i18n.global.t('errors.E044')
    )
    .on(
      v => v === 'E045',
      () => i18n.global.t('errors.E045')
    )
    .on(
      v => v === 'E046',
      () => i18n.global.t('errors.E046')
    )
    .on(
      v => v === 'E047',
      () => i18n.global.t('errors.E047')
    )
    .on(
      v => v === 'E048',
      () => i18n.global.t('errors.E048')
    )
    .on(
      v => v === 'E049',
      () => i18n.global.t('errors.E049')
    )
    .on(
      v => v === 'E050',
      () => i18n.global.t('errors.E050')
    )
    .on(
      v => v === 'E051',
      () => i18n.global.t('errors.E051')
    )
    .on(
      v => v === 'E052',
      () => i18n.global.t('errors.E052')
    )
    .on(
      v => v === 'E053',
      () => i18n.global.t('errors.E053')
    )
    .on(
      v => v === 'E054',
      () => i18n.global.t('errors.E054')
    )
    .on(
      v => v === 'E055',
      () => i18n.global.t('errors.E055')
    )
    .on(
      v => v === 'E056',
      () => i18n.global.t('errors.E056')
    )
    .on(
      v => v === 'E057',
      () => i18n.global.t('errors.E057')
    )
    .on(
      v => v === 'E058',
      () => i18n.global.t('errors.E058')
    )
    .on(
      v => v === 'E059',
      () => i18n.global.t('errors.E059')
    )
    .on(
      v => v === 'E060',
      () => i18n.global.t('errors.E060')
    )
    .on(
      v => v === 'E061',
      () => i18n.global.t('errors.E061')
    )
    .on(
      v => v === 'E062',
      () => i18n.global.t('errors.E062')
    )
    .on(
      v => v === 'E063',
      () => i18n.global.t('errors.E063')
    )
    .on(
      v => v === 'E064',
      () => i18n.global.t('errors.E064')
    )
    .on(
      v => v === 'E065',
      () => i18n.global.t('errors.E065')
    )
    .on(
      v => v === 'E066',
      () => i18n.global.t('errors.E066')
    )
    .on(
      v => v === 'E067',
      () => i18n.global.t('errors.E067')
    )
    .on(
      v => v === 'E068',
      () => i18n.global.t('errors.E068')
    )
    .on(
      v => v === 'E069',
      () => i18n.global.t('errors.E069')
    )
    .on(
      v => v === 'E070',
      () => i18n.global.t('errors.E070')
    )
    .otherwise(
      () => i18n.global.t('errors.E000')
    );
};

// REST
export default class ApiClient {
  public static async get(endpoint: string, params: { [key: string]: any } | null) {
    if (params) {
      try {
        const response = await client.get(
          `${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`,
          {
            headers: await getHeaders(),
            data: {},
          }
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    } else {
      try {
        const response = await client.get(`${baseUrl}${endpoint}`, {
          headers: await getHeaders(),
          data: {},
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    }
  }

  public static async post(endpoint: string, params: { [key: string]: any } | null, isAuth = true) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = process.env.VUE_APP_X_API_KEY;
    let headers: { [key: string]: string };
    if (isAuth) {
      headers = {
        'Content-Type': 'application/json',
        'X-API-KEY': xApiKey ? xApiKey : '',
        Authorization: `Bearer ${token}`,
        'Accept-Language': getAcceptLanguage(),
      };
    } else {
      headers = {
        'Content-Type': 'application/json',
        'Accept-Language': getAcceptLanguage(),
      };
    }
    return client
      .post(`${baseUrl}${endpoint}`, JSON.stringify(params), { headers })
      .then(response => response.data);
  }

  public static async put(endpoint: string, params: { [key: string]: any } | null) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = process.env.VUE_APP_X_API_KEY;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': xApiKey ? xApiKey : '',
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguage(),
    };
    if (params) {
      return client
        .put(`${baseUrl}${endpoint}`, JSON.stringify(params), { headers })
        .then(response => response.data);
    } else {
      return client.put(`${baseUrl}${endpoint}`, { headers }).then(response => response.data);
    }
  }

  public static async delete(endpoint: string, params: { [key: string]: any } | null) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = process.env.VUE_APP_X_API_KEY;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': xApiKey ? xApiKey : '',
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguage(),
    };
    if (params) {
      return client
        .delete(`${baseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`, {
          headers,
          data: {},
        })
        .then(response => response.data);
    } else {
      return client
        .delete(`${baseUrl}${endpoint}`, {
          headers,
          data: {},
        })
        .then(response => response.data);
    }
  }

  public static async memoruGet(endpoint: string, params: { [key: string]: any } | null) {
    if (params) {
      try {
        const response = await client.get(
          `${memoruBaseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`,
          {
            headers: await getMemoruHeaders(),
            data: {},
          }
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    } else {
      try {
        const response = await client.get(`${memoruBaseUrl}${endpoint}`, {
          headers: await getMemoruHeaders(),
          data: {},
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    }
  }

  public static async memoruPost(
    endpoint: string,
    params: { [key: string]: any } | null,
    isAuth = true
  ) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = memoruXkey;
    let headers: { [key: string]: string };
    if (isAuth) {
      headers = {
        'Content-Type': 'application/json',
        'X-API-KEY': xApiKey ? xApiKey : '',
        Authorization: `Bearer ${token}`,
        'Accept-Language': getAcceptLanguage(),
      };
    } else {
      headers = {
        'Content-Type': 'application/json',
        'Accept-Language': getAcceptLanguage(),
      };
    }
    return client
      .post(`${memoruBaseUrl}${endpoint}`, JSON.stringify(params), { headers })
      .then(response => response.data);
  }

  public static async memoruPut(endpoint: string, params: { [key: string]: any } | null) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = memoruXkey;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': xApiKey ? xApiKey : '',
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguage(),
    };
    if (params) {
      return client
        .put(`${memoruBaseUrl}${endpoint}`, JSON.stringify(params), { headers })
        .then(response => response.data);
    } else {
      return client.put(`${baseUrl}${endpoint}`, { headers }).then(response => response.data);
    }
  }

  public static async memoruDelete(endpoint: string, params: { [key: string]: any } | null) {
    const token = await auth0Module.getAuth0Token();
    const xApiKey = memoruXkey;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-KEY': xApiKey ? xApiKey : '',
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguage(),
    };
    if (params) {
      return client
        .delete(
          `${memoruBaseUrl}${endpoint}?${querystring.stringify(params, { skipNull: true })}`,
          {
            headers,
            data: {},
          }
        )
        .then(response => response.data);
    } else {
      return client
        .delete(`${memoruBaseUrl}${endpoint}`, {
          headers,
          data: {},
        })
        .then(response => response.data);
    }
  }
}
